import { useEffect, useRef, useState } from "react";

import { useAppSelector } from "@/redux/hooks";
import {
	GenerationJob,
	selectGenerationJobs,
} from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

/** How long the "AI drafts are ready" toast stays up after a generation finishes. */
const TOAST_MS = 4000;

const isReady = (job: GenerationJob): boolean => job.status === "COMPLETED" && !job.reviewedAt;

/**
 * One-shot "AI drafts are ready" toast (PREC-3635 P6 — "once it's done show …"):
 * watches the live generation-job map and flags the moment a job newly becomes
 * ready. The first run only seeds the baseline (jobs hydrated from GET /active
 * must not fire a toast on mount); only a genuine RUNNING/QUEUED→ready transition
 * afterward shows it. Returns whether the toast is visible.
 */
export const useFeedDraftsReadyToast = (): boolean => {
	const jobs = useAppSelector(selectGenerationJobs);

	const seenReadyRef = useRef<Set<number> | null>(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const readyIds = jobs.filter(isReady).map((job) => job.prospectId);

		// First run: seed the baseline without firing (already-ready jobs on mount).
		if (seenReadyRef.current === null) {
			seenReadyRef.current = new Set(readyIds);

			return;
		}

		const hasNewlyReady = readyIds.some((id) => !seenReadyRef.current?.has(id));

		seenReadyRef.current = new Set(readyIds);

		if (hasNewlyReady) {
			setVisible(true);
		}
	}, [jobs]);

	useEffect(() => {
		if (!visible) {
			return;
		}

		const timer = setTimeout(() => setVisible(false), TOAST_MS);

		return () => clearTimeout(timer);
	}, [visible]);

	return visible;
};
